import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowUpRight, Search } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { blogPosts } from '@/data/blog'
import { formatDate } from '@/lib/utils'
import { useSeo } from '@/hooks/useSeo'

export function BlogPage() {
  useSeo(
    'Writing',
    'Posts and published work on optimization, machine learning, supply chain, and the systems behind them.'
  )
  const [query, setQuery] = useState('')
  const [activeCategory, setActiveCategory] = useState<string>('all')

  const categories = useMemo(() => {
    const set = new Set(blogPosts.map((p) => p.category))
    return ['all', ...Array.from(set)]
  }, [])

  const posts = useMemo(() => {
    const q = query.trim().toLowerCase()
    return [...blogPosts]
      .filter((p) => activeCategory === 'all' || p.category === activeCategory)
      .filter((p) => {
        if (!q) return true
        return (
          p.title.toLowerCase().includes(q) ||
          p.excerpt.toLowerCase().includes(q) ||
          p.tags.some((t) => t.toLowerCase().includes(q))
        )
      })
      .sort((a, b) => +new Date(b.date) - +new Date(a.date))
  }, [query, activeCategory])

  return (
    <div className="container max-w-3xl pb-20 pt-12">
      <header className="mb-8 border-b border-border/40 pb-8">
        <h1 className="text-3xl font-semibold tracking-tight">Writing</h1>
        <p className="mt-2 text-muted-foreground">
          Notes on optimization, ML, and building things.
        </p>
      </header>

      <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search posts..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex flex-wrap gap-1">
          {categories.map((c) => (
            <Button
              key={c}
              variant={activeCategory === c ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setActiveCategory(c)}
              className="h-8 capitalize"
            >
              {c}
            </Button>
          ))}
        </div>
      </div>

      <ul className="divide-y divide-border/60">
        {posts.map((post) => {
          const isExternal = Boolean(post.externalUrl)
          const inner = (
            <>
              <div className="flex items-baseline justify-between gap-4">
                <h2 className="flex items-center gap-1.5 text-lg font-medium transition-colors group-hover:text-primary">
                  {post.title}
                  {isExternal && (
                    <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
                  )}
                </h2>
                <time className="shrink-0 text-xs tabular-nums text-muted-foreground">
                  {formatDate(post.date)}
                </time>
              </div>
              <p className="mt-1.5 text-sm text-muted-foreground">
                {post.excerpt}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {post.readTime > 0 && (
                  <>
                    <span className="text-xs text-muted-foreground">
                      {post.readTime} min read
                    </span>
                    <span aria-hidden className="text-muted-foreground/50">
                      &middot;
                    </span>
                  </>
                )}
                {post.tags.slice(0, 3).map((tag) => (
                  <Badge
                    key={tag}
                    variant="outline"
                    className="text-[11px] font-normal"
                  >
                    {tag}
                  </Badge>
                ))}
              </div>
            </>
          )
          return (
            <li key={post.id} className="group py-6 first:pt-0">
              {isExternal ? (
                <a
                  href={post.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  {inner}
                </a>
              ) : (
                <Link to={`/blog/${post.slug}`} className="block">
                  {inner}
                </Link>
              )}
            </li>
          )
        })}
      </ul>

      {posts.length === 0 && (
        <p className="py-12 text-center text-sm text-muted-foreground">
          No posts found.
        </p>
      )}
    </div>
  )
}
